import { Injectable, inject } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  selectAllEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import {
  EMPTY,
  Observable,
  expand,
  forkJoin,
  from,
  map,
  of,
  throwError,
  filter,
  BehaviorSubject,
} from 'rxjs';
import {
  catchError,
  combineLatestWith,
  mergeMap,
  switchMap,
  tap,
  toArray,
} from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { Signature } from '@digital-platform/docusign/domain';
import {
  DISPOSAL_SITE_STATE_CODES,
  IManifestRequest,
  IManifestRequestMeta,
  WasteProfileFlat,
} from '@digital-platform/manifests/domain';
import {
  FindManifestSearchApiResponse,
  IManifestType,
  ManifestModel,
} from '@digital-platform/manifests/domain';
import {
  ErrorService,
  LoadingService,
} from '@digital-platform/shared/services';
import { UserSettingsObject } from '@digital-platform/users/domain';
import {
  LoginSessionFacadeService,
  UserSettingsService,
} from '@digital-platform/users/services';
import {
  WasteProfileCustomerCard,
  WasteProfileStatus,
} from '@digital-platform/waste-profiles/domain';
import {
  WasteProfileCardsService,
  WasteProfilesService,
} from '@digital-platform/waste-profiles/services';
import { ManifestsService } from '../manifests/manifests.service';

interface IManifestStateProps {
  loading: boolean;
  loaded: boolean;
  editMode: boolean;
  active: IManifestRequest;
  selectedWasteProfiles: WasteProfileFlat[] | null;
  wasteProfiles: WasteProfileFlat[] | null;
}

const store = createStore(
  { name: 'manifests' },
  withEntities<ManifestModel>(),
  withProps<IManifestStateProps>({
    loading: false,
    loaded: false,
    wasteProfiles: null,
    selectedWasteProfiles: null,
    editMode: false,
    active: {
      id: null,
      manifest: null,
      meta: {
        getStartedStep: { completed: false, valid: false },
        generatorStep: { completed: false, valid: false },
        wasteProfilesStep: { completed: false, valid: false },
        transporterStep: { completed: false, valid: false },
        destinationStep: { completed: false, valid: false },
        // deliveryMethodStep: { completed: false, valid: false },
        signaturesStep: { completed: false, valid: false },
      },
    },
  })
);

const wasteProfilesStore = createStore(
  { name: 'wasteProfiles' },
  withEntities<WasteProfileCustomerCard>()
);

const wasteProfilesFlatStore = createStore(
  { name: 'wasteProfilesFlat' },
  withEntities<WasteProfileFlat>()
);

@Injectable({
  providedIn: 'root',
})
export class ManifestsFacadeService {
  public manifests$ = store.pipe(selectAllEntities());
  public activeManifestRequest$ = store.pipe(select(({ active }) => active));
  public editMode$ = store.pipe(select(({ editMode }) => editMode));
  public selectedWasteProfiles$ = store.pipe(
    select(({ selectedWasteProfiles }) => selectedWasteProfiles)
  );
  public loading$ = store.pipe(select(({ loading }) => loading));
  public loaded$ = store.pipe(select(({ loaded }) => loaded));
  public currentManifest$ = new BehaviorSubject<IManifestType>(null);
  private readonly errorService = inject(ErrorService);

  private readonly accessList$ = inject(LoginSessionFacadeService).accessList$;
  private readonly wasteProfilesService = inject(WasteProfilesService);
  private userSettingService$ = inject(UserSettingsService);

  private wpLoadingUuid: string;

  public wasteProfiles$ = wasteProfilesStore.pipe(selectAllEntities());

  constructor(private service: ManifestsService) {}

  public setSelectedWasteProfiles(
    selectedWasteProfiles: WasteProfileFlat[]
  ): void {
    store.update((state) => ({
      ...state,
      selectedWasteProfiles,
    }));
  }

  public clearActiveManifest(): void {
    store.update((state) => ({
      ...state,
      selectedWasteProfiles: null,
      editMode: false,
      active: {
        id: null,
        manifest: null,
        meta: null,
      },
    }));
  }

  public find(id: string): Observable<IManifestType | null> {
    if (!id) return of(null);
    return this.service.find(id).pipe(
      tap((manifest) => {
        store.update((state) => ({
          ...state,
          editMode: true,
          active: {
            ...state.active,
            meta: manifest?.metaInfo,
            manifest: {
              ...manifest,
            },
          },
        }));
      })
    );
  }

  public add(newManifest: IManifestType): Observable<IManifestType> {
    return this.service.create(newManifest).pipe(
      tap((manifest) =>
        store.update(upsertEntities(manifest), (state) => ({
          ...state,
          active: {
            ...state.active,
            manifest: manifest,
            id: manifest.id,
          },
        }))
      )
    );
  }

  public update(
    manifestId: string,
    updatedManifest: IManifestType
  ): Observable<IManifestType> {
    this.errorService.clearErrors();
    store.update((state) => ({ ...state, loading: true }));
    return this.service.update(manifestId, updatedManifest).pipe(
      tap((manifest) => {
        store.update(upsertEntities(manifest), (state) => ({
          ...state,
          loading: false,
          active: {
            ...state.active,
            manifest: manifest,
            meta: manifest.metaInfo,
            id: manifest.id,
          },
        }));
      })
    );
  }

  public search(
    searchBy: {
      key: string;
      value: string;
    }[]
  ) {
    this.searchLoop(searchBy)
      .pipe(
        map(
          (response: FindManifestSearchApiResponse): IManifestType[] =>
            response.Items
        )
      )
      .subscribe((manifests) => {
        if (searchBy?.length < 1 || searchBy[0].value === '') {
          store.update(upsertEntities(manifests), (state) => ({
            ...state,
          }));
        } else {
          //TODO: Need to clean up after facade integration. Keeping it for now for reference when we need to update the store.
          // store.update(setEntities(manifests), (state) => ({
          //   ...state,
          // }));
        }
      });
  }

  public searchLoop(searchBy) {
    return this.service.search(searchBy).pipe(
      expand((response) => {
        if (!response.LastEvaluatedKey) {
          //   // we got an empty response, nothing to do here
          return EMPTY;
        }

        return this.service.search(searchBy, response?.LastEvaluatedKey);
      })
    );
  }

  public validateAccessCode(accessCode: string): Observable<string> {
    return this.service.validateAccessCode(accessCode);
  }

  public validateProfileNumbers(profileNumbers: string[]) {
    return this.service.validateProfileNumbers(profileNumbers);
  }

  public upsert(id: string, updates: Partial<IManifestRequestMeta>) {
    store.update((state) => ({
      ...state,
      active: {
        ...state.active,
        manifest: {
          ...state.active.manifest,
          metaInfo: {
            ...updates,
          },
        },
        meta: {
          ...state.active.meta,
          ...updates,
        },
      },
    }));
  }

  public delete(id: string): Observable<IManifestType> {
    return this.service
      .delete(id)
      .pipe(tap((response) => store.update(deleteEntities(response.id))));
  }

  public loadManifests(accessList: string[]): void {
    store.destroy();
    store.update((state) => ({ ...state, loading: true }));
    if (accessList.includes('*')) {
      this.findAll('*').subscribe((manifests) => {
        store.update(upsertEntities(manifests));
        store.update((state) => ({ ...state, loading: false }));
      });
    } else {
      const observables = accessList.map((customerName) =>
        this.findAll(customerName)
      );
      forkJoin(observables)
        .pipe(
          mergeMap((results) => from(results).pipe(mergeMap((x) => x))),
          toArray()
        )
        .subscribe((manifests) => {
          store.update(upsertEntities(manifests));
          store.update((state) => ({ ...state, loading: false }));
        });
    }
  }

  public findAll(customerName?: string): Observable<IManifestType[]> {
    return this.service
      .findAll(customerName)
      .pipe(catchError((error) => throwError(() => error)));
  }

  public findWasteProfiles(): Observable<WasteProfileFlat[]> {
    const settings$ = this.userSettingService$.settings$.pipe(
      filter((settings) => !!settings)
    );

    const entities = wasteProfilesFlatStore.getValue().entities;
    if (Object.keys(entities).length > 0) {
      return of(Object.values(entities));
    } else {
      return this.accessList$.pipe(
        combineLatestWith(settings$),
        switchMap(([access, settings]) =>
          this.loadExternalCustomerCards(access, settings)
        ),
        map((response) => {
          wasteProfilesFlatStore.update(addEntities(response));
          return response;
        })
      );
    }
  }

  public loadExternalCustomerCards(
    accessList: string[],
    settings: Partial<UserSettingsObject>
  ): Observable<WasteProfileFlat[]> {
    let observable$: Observable<WasteProfileFlat[]>;

    if (settings.divisions && settings.divisions.length > 0) {
      this.wpLoadingUuid = uuidv4();
      observable$ = this.loadStatusCards(settings.divisions, accessList);
    } else if (accessList.length > 0) {
      this.wpLoadingUuid = uuidv4();
      observable$ = this.loadStatusCards([], accessList);
    } else {
      // If neither condition is met, return an empty Observable
      observable$ = of([]);
    }

    return observable$;
  }

  public loadStatusCards(divisionNumbers: string[], accessList: string[]) {
    const calls = [];
    if (divisionNumbers && divisionNumbers.length > 0) {
      divisionNumbers.forEach((divisionNumber) => {
        calls.push(this.statusCardsLoadingCalls(null, divisionNumber));
      });
    } else if (accessList.includes('*')) {
      calls.push(this.statusCardsLoadingCalls('*'));
    } else {
      accessList.forEach((customerName) => {
        calls.push(this.statusCardsLoadingCalls(customerName));
      });
    }
    return forkJoin(calls).pipe(
      map(() => {
        const entities = wasteProfilesStore.getValue().entities;

        const wasteProfilesFlat: WasteProfileFlat[] = Object.values(entities)
          .filter((entity) => new Date(entity.expiryDate) > new Date())
          .filter((entity) =>
            DISPOSAL_SITE_STATE_CODES.some(
              (state) => state.code === entity.destination?.address.stateCode
            )
          )
          .filter((entity) => entity.name && !/asbestos/i.test(entity.name))
          .map((entity) => ({
            id: entity.id,
            selected: false,
            generatorName: entity.generator?.name,
            profileNumber: entity.number,
            profileName: entity.name,
            envelopeId: '',
            approvedDate: entity.approvedDate,
            expirationDate: entity.expiryDate,
            destination: entity.destination?.name,
            destinationAddress: entity.destination?.address,
            siteAddress: entity.generator?.siteAddress,
            mailingAddress: entity.generator?.generatorAddress,
            epaId: entity.generator?.epaId,
            contactPhone: entity.generator?.contact?.phone,
            docusignData: entity.docusignData,
          }));

        return wasteProfilesFlat;
      })
    );
  }

  public statusCardsLoadingCalls(
    customerName?: string,
    divisionNumber?: string
  ) {
    return forkJoin([
      this.loadStatusCardsLoop(
        WasteProfileStatus.APPROVED,
        customerName,
        divisionNumber
      ),
      this.loadStatusCardsLoop(
        WasteProfileStatus.EXPIRING,
        customerName,
        divisionNumber
      ),
    ]);
  }

  public rejectManifest(
    id: string,
    discrepancyNotes: string
  ): Observable<IManifestType> {
    return this.service.rejectManifest(id, discrepancyNotes);
  }
  public resendEmail(id: string, signature: Signature): Observable<Signature> {
    return this.service.resendEmail(id, signature);
  }

  private loadStatusCardsLoop(
    status: WasteProfileStatus,
    customerName?: string,
    divisionNumber?: string
  ) {
    return this.wasteProfilesService
      .findAllStatusCards({
        status,
        customerName,
        divisionNumber,
        wpLoadingUuid: this.wpLoadingUuid,
      })
      .pipe(
        catchError((error) => {
          this.errorService.raiseError(error);
          return EMPTY;
        }),
        expand((response) => {
          if (response?.Items && response.Items.length > 0) {
            response.Items.forEach((item) => {
              wasteProfilesStore.update(addEntities(item));
            });
          }
          if (!response.LastEvaluatedKey) {
            // no LastEvaluatedKey means no additional pages
            return EMPTY;
          }

          return this.wasteProfilesService.findAllStatusCards({
            status,
            customerName,
            divisionNumber,
            ExclusiveStartKey: response?.LastEvaluatedKey,
            wpLoadingUuid: this.wpLoadingUuid,
          });
        })
      );
  }
}
